import Highcharts from 'highcharts';
import { MAX_LABELS, MAX_READ_POINTS, MAX_READ_SERIES } from './screen-inventory.constants';
import type { InventoryCandidate, InventoryDetail, InventoryLabel, PageDataResult } from './screen-inventory.types';
import { isText } from './screen-inventory.utils';

const POINT_NAMED_TYPES = new Set(['pie', 'variablepie', 'treemap', 'sunburst', 'funnel', 'pyramid', 'item', 'wordcloud']);

function dataLength (series: Highcharts.Series): number {
    return (series.options as { data?: unknown[] }).data?.length ?? 0;
}

function describeShape (chart: Highcharts.Chart): string {
    const types = [...new Set(chart.series.map((series) => series.type))];
    const xAxisOptions = chart.xAxis[0]?.options;
    const pointCount = Math.max(0, ...chart.series.map(dataLength));
    const shape = [types.join('/') || 'chart'];

    if (xAxisOptions?.type === 'datetime') shape.push('over time');
    if (xAxisOptions?.categories?.length) shape.push(`across ${xAxisOptions.categories.length} categories`);
    shape.push(`${pointCount} ${pointCount === 1 ? 'point' : 'points'}`, `${chart.series.length} series`);

    return shape.join(', ');
}

function describeAxes (chart: Highcharts.Chart): Pick<InventoryCandidate, 'details'> {
    const x = chart.xAxis[0]?.options.title?.text;
    const y = chart.yAxis[0]?.options.title?.text;
    const details: InventoryDetail[] = [
        ...(isText(x) ? [{ name: 'x-axis', text: x }] : []),
        ...(isText(y) ? [{ name: 'y-axis', text: y }] : []),
    ];

    return details.length ? { details } : {};
}

// Series names come from options, because Highcharts names an unnamed series "Series 1".
function describeLabels (chart: Highcharts.Chart): Pick<InventoryCandidate, 'labelsOf' | 'labels' | 'labelTotal'> {
    const [first] = chart.series;

    if (chart.series.length === 1 && POINT_NAMED_TYPES.has(first.type)) {
        const namedPoints = (first.points ?? []).filter((point) => isText(point.name));
        const labels: InventoryLabel[] = namedPoints
            .slice(0, MAX_LABELS)
            .map((point) => ({ text: point.name, ...(!point.visible && { hidden: true }) }));

        return { labelsOf: 'points', labels, labelTotal: namedPoints.length };
    }

    const named = chart.series.filter((series) => isText(series.options.name));
    const labels = named
        .slice(0, MAX_LABELS)
        .map((series) => ({ text: series.options.name as string, ...(!series.visible && { hidden: true }) }));

    return { labelsOf: 'series', labels, labelTotal: named.length };
}

type ReadPoint = Record<string, unknown> & { name?: string; x?: unknown };

function axisValue (axis: Highcharts.Axis | undefined, value: unknown): unknown {
    if (typeof value !== 'number' || !axis) return value;

    const { type, categories } = axis.options;
    if (Number.isInteger(value) && categories?.[value] !== undefined) return categories[value];
    if (type === 'datetime' && Number.isFinite(value)) return new Date(value).toISOString();

    return value;
}

function defaultX (series: Highcharts.Series, index: number): number {
    const { pointStart, pointInterval = 1 } = series.options as { pointStart?: number | string; pointInterval?: number };
    const start = typeof pointStart === 'string' ? series.chart.time.parse(pointStart) : pointStart;

    return (start ?? 0) + index * pointInterval;
}

function valueKeys (series: Highcharts.Series): string[] {
    return (series as { pointArrayMap?: string[] }).pointArrayMap ?? ['y'];
}

// Mirrors Highcharts' Point.optionsToObject: an array has a leading x or name only when it is longer than the value keys.
function toPointObject (series: Highcharts.Series, datum: unknown, keys: string[]): Record<string, unknown> {
    if (Array.isArray(datum)) {
        const { keys: optionKeys } = series.options as { keys?: string[] };
        if (optionKeys) return Object.fromEntries(optionKeys.map((key, index) => [key, datum[index]]));

        const hasLead = datum.length > keys.length;
        const values = hasLead ? datum.slice(1) : datum;
        const point = Object.fromEntries(keys.map((key, index) => [key, values[index]]));
        if (!hasLead) return point;

        const [first] = datum;

        return typeof first === 'string' && series.xAxis?.options.type !== 'datetime' ? { ...point, name: first } : { ...point, x: first };
    }

    if (datum !== null && typeof datum === 'object') return datum as Record<string, unknown>;

    return { [keys[0]]: datum };
}

function readDatum (series: Highcharts.Series, datum: unknown, index: number): ReadPoint {
    // Pie charts have no x-axis, even though the Highcharts type says they always do.
    const { xAxis: axis, yAxis } = series as { xAxis?: Highcharts.Axis; yAxis?: Highcharts.Axis };
    const toX = (x: unknown) => axisValue(axis, typeof x === 'string' && axis?.options.type === 'datetime' ? series.chart.time.parse(x) : x);
    const keys = valueKeys(series);
    const point = toPointObject(series, datum, keys);
    const { name, x } = point;
    const nameReplacesX = Array.isArray(datum) && isText(name) && x === undefined;
    const values = Object.fromEntries(keys.map((key) => [key, key === 'y' ? axisValue(yAxis, point[key] ?? null) : (point[key] ?? null)]));

    return {
        ...(isText(name) && { name }),
        ...(axis && !nameReplacesX && { x: toX(x ?? defaultX(series, index)) }),
        ...values,
    };
}

function sampleIndexes (length: number, limit: number): number[] {
    if (length <= limit) return [...Array(length).keys()];

    return [...Array(limit).keys()].map((index) => Math.round((index * (length - 1)) / (limit - 1)));
}

function readSeries (series: Highcharts.Series, position: number, notes: string[]) {
    const data = (series.options as { data?: unknown[] }).data ?? [];
    const indexes = sampleIndexes(data.length, MAX_READ_POINTS);
    const { name } = series.options;

    if (indexes.length < data.length) {
        const label = isText(name) ? `"${name}"` : String(position + 1);
        notes.push(`Series ${label} has ${data.length} points; ${indexes.length} are included, sampled evenly, keeping the first and last.`);
    }

    return {
        ...(isText(name) && { name }),
        type: series.type,
        visible: series.visible,
        pointCount: data.length,
        points: indexes.map((index) => readDatum(series, data[index], index)),
    };
}

function readVisibleRange (chart: Highcharts.Chart) {
    const [axis] = chart.xAxis;
    if (!axis) return {};

    const { min, max, dataMin, dataMax } = axis.getExtremes();
    if (!Number.isFinite(min) || !Number.isFinite(max)) return {};

    return { visibleRange: { from: axisValue(axis, min), to: axisValue(axis, max), zoomed: min > dataMin || max < dataMax } };
}

function readChart (chart: Highcharts.Chart): PageDataResult {
    const notes: string[] = [];
    const kept = chart.series.slice(0, MAX_READ_SERIES);
    if (kept.length < chart.series.length) {
        notes.push(`The chart has ${chart.series.length} series; only the first ${kept.length} are included.`);
    }
    const series = kept.map((item, position) => readSeries(item, position, notes));

    return {
        ...describeAxes(chart),
        ...readVisibleRange(chart),
        seriesCount: chart.series.length,
        series,
        ...(notes.length && { notes }),
    };
}

function describeChart (chart: Highcharts.Chart): InventoryCandidate {
    const { options } = chart;
    const title = options.title?.text;
    const description = options.accessibility?.description || options.caption?.text;
    const fallbackName = chart.series.map((series) => series.options.name).find(isText);

    return {
        element: chart.container,
        kind: 'chart',
        ...(isText(title) && { title }),
        ...(fallbackName && { fallbackName }),
        ...(isText(description) && { description }),
        shape: describeShape(chart),
        ...describeAxes(chart),
        ...describeLabels(chart),
        read: () => readChart(chart),
    };
}

export function collectChartCandidates (): InventoryCandidate[] {
    return Highcharts.charts
        .filter((chart): chart is Highcharts.Chart => Boolean(chart?.container?.isConnected))
        .map(describeChart);
}
